import React from 'react';
import { Link } from 'react-router-dom';
import { Sprout, CheckCircle, Edit, Image, Calendar, TrendingUp, Users } from 'lucide-react';
import SEO from './SEO';

export default function Welcome() {
  return (
    <>
      <SEO
        title="Welcome to Local Artist Fairs"
        description="Your artist fair has been indexed! Learn how to claim your listing and manage your artist fair profile."
        keywords="artist fair claim, claim listing, artist fair owner, local agriculture"
      />

      <div className="min-h-screen bg-teal-50">
        {/* Hero Section */}
        <div className="bg-primary-dark text-white py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="flex justify-center mb-6">
                <div className="bg-white rounded-full p-6">
                  <Sprout className="h-16 w-16 text-primary-dark" />
                </div>
              </div>
              <h1 className="text-5xl font-bold mb-4">
                Welcome to Local Artist Fairs!
              </h1>
              <p className="text-2xl text-teal-100">
                Your artist fair has been indexed in our community directory
              </p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto">
            {/* What This Means Section */}
            <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
              <h2 className="text-3xl font-bold text-primary-dark mb-4">
                What This Means
              </h2>
              <p className="text-lg text-gray-700 mb-4">
                We're building a comprehensive directory of local artist fairs to help customers discover fresh, locally-grown produce and products in their community. Your artist fair has been added to our database and is currently under review.
              </p>
              <p className="text-lg text-gray-700">
                <strong>LocalFairs.com</strong> connects local farms with customers who value fresh, sustainable, and locally-sourced agriculture. It's completely <strong className="text-primary-dark">free for artist fairs</strong>!
              </p>
            </div>

            {/* Review Process Section */}
            <div className="bg-moonbone border-l-4 border-dusty-teal p-6 mb-8">
              <h2 className="text-2xl font-bold text-ink mb-3 flex items-center">
                <CheckCircle className="h-6 w-6 mr-2" />
                Review Process
              </h2>
              <div className="text-gray-700 space-y-2">
                <p>
                  <strong>Status:</strong> Your artist fair is currently under review
                </p>
                <p>
                  <strong>Timeline:</strong> Typically reviewed within 48-72 hours
                </p>
                <p>
                  <strong>What we verify:</strong> Location accuracy, contact information, and general details
                </p>
                <p className="text-primary-dark font-semibold mt-4">
                  You'll receive a notification when your listing goes live!
                </p>
              </div>
            </div>

            {/* Benefits Section */}
            <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
              <h2 className="text-3xl font-bold text-primary-dark mb-6">
                Benefits of Claiming Your Listing
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-start space-x-3">
                  <Edit className="h-6 w-6 text-primary-dark flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-lg text-gray-900">Update Details</h3>
                    <p className="text-gray-600">Keep your artist fair information current anytime</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Sprout className="h-6 w-6 text-primary-dark flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-lg text-gray-900">Manage Products</h3>
                    <p className="text-gray-600">Add, edit, and update your products and pricing</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Image className="h-6 w-6 text-primary-dark flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-lg text-gray-900">Upload Photos</h3>
                    <p className="text-gray-600">Showcase your artist fair with beautiful images</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Calendar className="h-6 w-6 text-primary-dark flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-lg text-gray-900">Manage Availability</h3>
                    <p className="text-gray-600">Update hours and seasonal availability</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Users className="h-6 w-6 text-primary-dark flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-lg text-gray-900">Connect with Customers</h3>
                    <p className="text-gray-600">Build your local customer base</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <TrendingUp className="h-6 w-6 text-primary-dark flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-lg text-gray-900">Increase Visibility</h3>
                    <p className="text-gray-600">Get discovered by new customers in your area</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-teal-700 to-teal-900 text-white rounded-lg shadow-xl p-8 mb-8 text-center">
              <h2 className="text-3xl font-bold mb-4">Ready to Claim Your Artist Fair?</h2>
              <p className="text-xl text-teal-100 mb-6">
                Create an account or log in to get started
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/signup"
                  className="bg-white text-primary-dark px-8 py-4 rounded-lg font-bold text-lg hover:bg-secondary-light/50 transition-colors shadow-lg"
                >
                  Create Account
                </Link>
                <Link
                  to="/login"
                  className="bg-primary text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-secondary-light/500 transition-colors border-2 border-white"
                >
                  Log In to Claim
                </Link>
              </div>
            </div>

            {/* FAQ Section */}
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-3xl font-bold text-primary-dark mb-6">
                Frequently Asked Questions
              </h2>
              <div className="space-y-6">
                <div>
                  <h3 className="font-bold text-xl text-gray-900 mb-2">
                    What is LocalFairs.com?
                  </h3>
                  <p className="text-gray-700">
                    We're a free community directory connecting local artist fairs with customers who want to support local agriculture. We help people find fresh, locally-grown produce and products in their area.
                  </p>
                </div>

                <div>
                  <h3 className="font-bold text-xl text-gray-900 mb-2">
                    Why was my artist fair indexed?
                  </h3>
                  <p className="text-gray-700">
                    We actively index artist fairs, farmers markets, and agricultural businesses to build a comprehensive directory for our community. Our goal is to make it easier for customers to discover and support local agriculture.
                  </p>
                </div>

                <div>
                  <h3 className="font-bold text-xl text-gray-900 mb-2">
                    What happens during the review?
                  </h3>
                  <p className="text-gray-700">
                    We verify your location, contact information, and ensure all details are accurate. This typically takes 48-72 hours. Once approved, your listing will be publicly visible on LocalFairs.com.
                  </p>
                </div>

                <div>
                  <h3 className="font-bold text-xl text-gray-900 mb-2">
                    What if my information is incorrect?
                  </h3>
                  <p className="text-gray-700">
                    No problem! Once you claim your listing, you can update any information. You can also contact us at{' '}
                    <a href="/contact" className="text-primary-dark hover:underline">
                      our contact page
                    </a>{' '}
                    if you need immediate assistance.
                  </p>
                </div>

                <div>
                  <h3 className="font-bold text-xl text-gray-900 mb-2">
                    Is there a cost?
                  </h3>
                  <p className="text-gray-700 font-semibold text-primary-dark">
                    No! LocalFairs.com is completely FREE for artist fairs. Our mission is to support local agriculture and connect farms with their communities.
                  </p>
                </div>

                <div>
                  <h3 className="font-bold text-xl text-gray-900 mb-2">
                    How do I claim my artist fair?
                  </h3>
                  <p className="text-gray-700">
                    Simply create an account (or log in if you already have one), then navigate to your artist fair's listing and click "Claim This Artist Fair." We'll verify your ownership and grant you access to manage your listing.
                  </p>
                </div>
              </div>
            </div>

            {/* Contact Section */}
            <div className="mt-8 text-center text-gray-600">
              <p>
                Have more questions?{' '}
                <Link to="/contact" className="text-primary-dark hover:underline font-semibold">
                  Contact us
                </Link>
                {' '}— we're here to help!
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
